<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { cloneDeep } from 'lodash-es'
    import * as yup from 'yup'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { asAny } from 'hydria/svelte_helpers'

    import type { IAddress, IAddressGroup } from 'wdom/services/contacts'

    import type { User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import ContactEditor from 'wdom/components/contacts/ContactEditor.svelte'
    import Country from 'wdom/components/contacts/Country.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'
    import PostalCode from 'wdom/components/contacts/PostalCode.svelte'
    import Province from 'wdom/components/contacts/Province.svelte'

    const BASE_FIELDS = ['name', 'org', 'city', 'sp', 'postal_code', 'country', 'voice', 'fax', 'mobile', 'street', 'accounting_comment']
    const BASE_REQ_FIELDS = ['name', 'city', 'country', 'street', 'any-phone']
    const GENERAL_FIELDS = ['title', 'comment', 'name', 'org', 'email', 'city', 'sp', 'postal_code', 'country', 'voice', 'fax', 'mobile', 'street', 'accounting_comment']
    const GENERAL_REQ_FIELDS = ['title', 'name', 'city', 'country', 'street', 'any-phone']

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)
    const addresses = sderived(userh.addresses, ($addresses) => $addresses)

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const selectedAddress = getContext('selectedAddress') as Writable<IAddress | null>

    let working: false | 'default' | 'email' | 'address' = $state(false)
    let modal: null | 'email' | 'delete' = $state(null)

    let focusElement: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (focusElement) focusElement.focus()
    })

    let newEmailAddress: string | null = $state('')
    const newEmailSchema = yup.string().email().required()
    let newEmailValid = $derived(newEmailSchema.isValidSync(newEmailAddress))

    unsubs.push(
        addresses.subscribe(($addresses) => {
            if ($addresses) $selectedAddress = $addresses[0]
            else $selectedAddress = null
        }),
    )

    function openEmailModal() {
        modal = 'email'
        newEmailAddress = ''
    }

    async function doChangeEmail() {
        modal = null

        working = 'email'
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/email`, { email: newEmailAddress })
        } catch (e: any) {
            console.error('Failed to set email:', e)
            notify({
                type: 'danger',
                title: 'Adresse électronique',
                html: `<p>La sauvegarde n’a pas pu être effectuée :</p><p>${e && e.data && e.data.error ? e.data.error : 'Erreur inconnue.'}</p>`,
            })

            modal = 'email'
            working = false
            return
        }

        newEmailAddress = null
        userh.raw.item.email = r.data.item.email
        userh.raw.addresses[0].email = r.data.item.email
        userh.loadUserResponse()
        userh.loadAddressData()
        $selectedAddress = $addresses[0]
        notify({
            type: 'success',
            title: 'Adresse électronique',
            text: 'Adresse sauvegardée.',
        })
        working = false
    }

    async function setAsDefault() {
        working = 'default'

        const currentSelectionId = ($selectedAddress as IAddress).id

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('PUT', $selectedAddress === $addresses[0] ? `/json/wdom-user/1/${$user.id}/address/main/default` : `/json/wdom-user/1/${$user.id}/address/${currentSelectionId}/default`)
        } catch (e: any) {
            r = e
            console.error('Failed to set default address:', e)
            notify({
                type: 'danger',
                title: 'Adresse',
                text: 'L’adresse par défaut n’a pas pu être configurée.',
            })
        }

        let newSel: IAddress | null = null
        for (const adr of $addresses) {
            if (adr.id === currentSelectionId) {
                newSel = adr
                break
            }
        }

        if (r) {
            userh.raw.addresses = r.data.addresses
            userh.loadAddressData()
            notify({
                type: 'success',
                title: 'Adresse',
                text: 'Succès du changement de l’adresse par défaut.',
            })
        }

        if (!newSel) newSel = $addresses[0]
        $selectedAddress = newSel

        working = false
    }

    let contactGroupEdit: IAddressGroup | undefined = $state()
    let editIsNewAddress = false
    function editAddress() {
        editIsNewAddress = false
        let fields = [...($selectedAddress === $addresses[0] ? BASE_FIELDS : GENERAL_FIELDS)]
        let reqFields = [...($selectedAddress === $addresses[0] ? BASE_REQ_FIELDS : GENERAL_REQ_FIELDS)]
        let vals = cloneDeep($selectedAddress) as IAddress
        for (const key of fields) {
            if (key == 'street') continue
            if (vals[key] == null) {
                vals[key] = ''
            }
        }
        let cgroup = {
            source: vals,
            // fieldStatus: {},
            // hiddenFields: [],
            fields: new Set(fields),
            requiredFields: new Set(reqFields),
            edit: cloneDeep(vals),
            invalidFields: {
                contextual: new Set(),
            },
        }
        contactGroupEdit = cgroup as unknown as IAddressGroup
    }

    function addAddress() {
        editIsNewAddress = true
        let fields = [...GENERAL_FIELDS]
        let reqFields = [...GENERAL_REQ_FIELDS]
        let vals = {}
        for (const key of fields) {
            if (key == 'street') {
                vals[key] = []
            } else {
                vals[key] = ''
            }
        }
        let cgroup = {
            source: vals,
            // fieldStatus: {},
            // hiddenFields: [],
            fields: new Set(fields),
            requiredFields: new Set(reqFields),
            edit: cloneDeep(vals),
            invalidFields: {
                contextual: new Set(),
            },
        }
        contactGroupEdit = cgroup as unknown as IAddressGroup
    }

    async function saveEdition(cg: any) {
        const edit = cg.edit
        const cgroup = contactGroupEdit

        let fields = [...(!editIsNewAddress && $selectedAddress === $addresses[0] ? BASE_FIELDS : GENERAL_FIELDS)]
        let reqFields = [...(!editIsNewAddress && $selectedAddress === $addresses[0] ? BASE_REQ_FIELDS : GENERAL_REQ_FIELDS)]

        const data = cloneDeep(edit)
        for (const key of fields) {
            if (key != 'street' && !data[key]) {
                data[key] = null
            }
        }

        contactGroupEdit = undefined

        working = 'address'
        const selAddrId = ($selectedAddress as IAddress).id
        let currentSelectionId = !editIsNewAddress && selAddrId ? selAddrId : null
        let existingAddressIds = $addresses.map((a) => a.id).filter((a) => a)

        let r: SimpleFetchResult
        try {
            if (editIsNewAddress) {
                r = await simpleFetch('POST', `/json/wdom-user/1/${$user.id}/address`, { address: data })
            } else if ($selectedAddress === $addresses[0]) {
                r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/address`, { address: data })
            } else {
                r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/address/${selAddrId}`, { address: data })
            }
        } catch (e: any) {
            r = e
            console.error('Error saving address:', e)
            notify({
                type: 'danger',
                title: $_('Address'),
                text: $_('The address information could not be saved.'),
            })

            contactGroupEdit = cgroup
        }
        working = false

        if (r) {
            notify({
                type: 'success',
                title: $_('Address'),
                text: $_('The new address information has been saved.'),
            })

            userh.raw.addresses = r.data.addresses
            userh.loadAddressData()

            let newSel: IAddress | null = null
            if (currentSelectionId) {
                for (const adr of $addresses) {
                    if (adr.id === currentSelectionId) {
                        newSel = adr
                        break
                    }
                }
            } else if (editIsNewAddress) {
                for (const adr of $addresses.slice(1)) {
                    if (existingAddressIds.includes(adr.id)) continue
                    newSel = adr
                    break
                }
            }
            if (!newSel) newSel = $addresses[0]
            $selectedAddress = newSel
        }
    }

    async function deleteAddress() {
        working = 'address'
        modal = null

        const selAddr = $selectedAddress as IAddress

        let r: any
        try {
            r = await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}/address/${selAddr.id}`)
        } catch (e) {
            r = null
            console.error('Error deleting address:', e)
            notify({
                type: 'danger',
                title: $_('Address'),
                text: $_('The address information could not be deleted.'),
            })
        }
        working = false

        if (r) {
            notify({
                type: 'success',
                title: $_('Address'),
                text: selAddr.title ? `L’adresse « ${selAddr.title} » est détruite.` : 'L’adresse est détruite.',
            })

            userh.raw.addresses = r.data.addresses
            userh.loadAddressData()

            $selectedAddress = $addresses[0]
        }
    }

    $effect(() => {
        if (modal !== null && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (modal == null && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
    onDestroy(() => {
        if (modal !== null && document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })
</script>

<style>
    dd {
        margin-bottom: 0;
    }

    dt:not(:first-child) {
        margin-top: 0.5rem;
    }
</style>

<div class="card">
    <div class="card-title text-primary">
        {#if $status == 'ready' && $addresses}
            <div class="address-list-head">
                {#each $addresses as adr, i}
                    <button type="button" class="btn btn-link btn-a text-primary addr-{i}" class:base={i == 0} class:active={$selectedAddress === adr} onclick={() => ($selectedAddress = adr)}>
                        {adr.title}
                        {#if adr.is_default_billing}
                            <span class="is-billing"><Icon inline icon="fa:dollar" /></span>
                        {/if}
                    </button>
                {/each}
            </div>
        {:else}
            {$_('User address')}
        {/if}
    </div>

    {#if $status == 'loading' || $loading || !$selectedAddress}
        <div class="card-body">
            <div class="row">
                <div class="col-md-8">
                    <LoadingPattern pattern={[20, 45, 20, 45, 20, 60, 20, 45, 45]} />
                </div>
                <div class="col-md-4">
                    <LoadingPattern pattern={[20, 45, 20, 45, '-', 20, 50]} />
                </div>
            </div>
            <LoadingPattern pattern={['-', ['flex', ['-:1 0 0', 'b:0 0 60px', 'b:0 0 60px']]]} />
        </div>
    {:else}
        <div class="card-body">
            <div class="row">
                <div class="col-md-8">
                    <dl>
                        <dt>{$_('Full name')}</dt>
                        <dd>{$selectedAddress.name ? $selectedAddress.name : '–'}</dd>
                        <dt>{$_('Organization')}</dt>
                        <dd>{$selectedAddress.org ? $selectedAddress.org : '–'}</dd>

                        <dt>{$_('Email address')}</dt>
                        <dd>
                            {$selectedAddress.email ? $selectedAddress.email : '–'}
                            {#if $selectedAddress === $addresses[0]}
                                <button type="button" class="btn btn-outline-secondary btn-sm" disabled={!!working} onclick={() => openEmailModal()}>
                                    {#if working === 'email'}
                                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                    {:else}
                                        Changer
                                    {/if}
                                </button>
                            {/if}
                        </dd>

                        <dt>{$_('Address')}</dt>
                        {#if $selectedAddress.street && $selectedAddress.street.length}
                            {#each $selectedAddress.street as street}
                                <dd>{street}</dd>
                            {/each}
                        {:else}
                            <dd>–</dd>
                        {/if}

                        <dd>{$selectedAddress.city ? $selectedAddress.city : '<city>'}, <Province contact={$selectedAddress} /></dd>
                        <dd><PostalCode contact={$selectedAddress} />, <Country contact={$selectedAddress} /></dd>
                    </dl>
                </div>
                <div class="col-md-4">
                    <dl>
                        {#if $selectedAddress.voice}
                            <dt>{$_('Voice')}</dt>
                            <dd><InternationalPhone phone={$selectedAddress.voice} /></dd>
                        {/if}
                        {#if $selectedAddress.mobile}
                            <dt>{$_('Mobile')}</dt>
                            <dd><InternationalPhone phone={$selectedAddress.mobile} /></dd>
                        {/if}
                        {#if $selectedAddress.fax}
                            <dt>{$_('FAX')}</dt>
                            <dd><InternationalPhone phone={$selectedAddress.fax} /></dd>
                        {/if}
                    </dl>
                    <dl style="margin-top: 1em">
                        <dt>Acomba</dt>
                        <dd>{$selectedAddress.accounting_comment ? $selectedAddress.accounting_comment : '–'}</dd>
                    </dl>
                </div>
            </div>
        </div>
        <div class="card-footer text-bg-light text-end">
            {#if !$selectedAddress.is_default_billing}
                <button type="button" class="btn btn-outline-secondary" disabled={!!working} onclick={() => setAsDefault()}>
                    {#if working === 'default'}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        Un moment…
                    {:else}
                        {$_('Make default billing')}
                    {/if}
                </button>
            {/if}
            {#if $selectedAddress !== $addresses[0]}
                <button type="button" class="btn btn-outline-secondary" disabled={!!working} onclick={() => (modal = 'delete')}>{$_('Delete')}</button>
            {/if}
            <button type="button" class="btn btn-outline-secondary" disabled={!!working} onclick={() => editAddress()}>{$_('Edit')}</button>
            <button type="button" class="btn btn-outline-secondary" disabled={!!working} onclick={() => addAddress()}>{$_('Add')}</button>
        </div>
    {/if}
</div>

{#if modal === 'email'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Changer l’adresse électronique principale')}</div>
                </div>
                <form
                    class="form-horizontal"
                    onsubmit={(e) => {
                        e.preventDefault()
                        doChangeEmail()
                    }}
                >
                    <div class="modal-body d-grid gap-2">
                        <div class="row">
                            <label for="nothing" class="col-sm-4 col-form-label">Adresse actuelle</label>
                            <div class="col-sm-8"><input type="text" class="form-control" disabled value={$addresses[0].email} /></div>
                        </div>

                        <div class="row">
                            <label for="new-email" class="col-sm-4 col-form-label">Nouvelle adresse</label>
                            <div class="col-sm-8"><input type="text" class="form-control" id="new-email" bind:this={focusElement} bind:value={newEmailAddress} /></div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-secondary" onclick={() => (modal = null)}>{$_('Cancel')}</button>
                        <button type="submit" class="btn btn-primary" disabled={!newEmailValid || newEmailAddress === $addresses[0].email}>{$_('Save')}</button>
                    </div>
                </form>
            </div>
        </div>
        <div
            aria-hidden="true"
            class="modal-backdrop"
            onclick={() => {
                modal = null
            }}
        ></div>
    </div>
{:else if modal === 'delete'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>Détruire une adresse</div>
                </div>
                <div class="modal-body">
                    {#if asAny($selectedAddress).title}
                        <p>Êtes-vous certain de vouloir détruire l’adresse titrée « {asAny($selectedAddress).title} » ?</p>
                    {:else}
                        <p>Êtes-vous certain de vouloir détruire cette adresse ?</p>
                    {/if}
                </div>
                <div class="modal-footer text-end">
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (modal = null)}>Annuler</button>
                    <button class="btn btn-danger" type="button" onclick={() => deleteAddress()}>Oui, détruire</button>
                </div>
            </div>
        </div>
        <div
            aria-hidden="true"
            class="modal-backdrop"
            onclick={() => {
                modal = null
            }}
        ></div>
    </div>
{/if}

{#if contactGroupEdit != null}
    <ContactEditor
        context="account"
        alignRight={true}
        contactGroup={asAny(contactGroupEdit)}
        close={() => {
            contactGroupEdit = undefined
        }}
        save={(cg) => {
            saveEdition(cg)
        }}
    />
{/if}
